import {
  Button,
  Card,
  Combobox,
  HelpText,
  Icon,
  Label,
  Tag,
  TextField,
  UtilityButton,
} from '@lego/connect-components-react';
import type { ReactNode } from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormFieldState, FormVariableState } from '@/types/types';
import { cn } from '@/utils/cn';
import { VariableActionsPanel } from './FormVariableActions';
import { FieldValidationInfo } from './FormVariableFieldInfo';
import { FormVariableRegex } from './FormVariableRegex';
import { FieldGroup, getVariableTypeOptions, inferVariableType } from './formVariableUtils';

type VariableCardProps = {
  variable: FormVariableState;
  fields: FormFieldState[];
  submitAttempted: boolean;
  portalTarget?: Element | null;
  number: number;
  onChange: (id: string, value: Record<string, unknown>) => void;
  onRemove: (id: string) => void;
};

const renderFieldOptions = (fields: FormFieldState[]) =>
  fields.map((field) => (
    <Combobox.Option key={field.id} value={field.id ?? ''}>
      <span className="flex w-full flex-wrap items-center justify-between gap-150">
        {field.name}
        <FieldValidationInfo field={field} className="justify-end" />
      </span>
    </Combobox.Option>
  ));

const CardFieldSelector = ({
  variable,
  field,
  fields,
  submitAttempted,
  portalTarget,
  onChange,
  tags,
  contentId,
  onCollapse,
}: {
  variable: FormVariableState;
  field?: FormFieldState;
  fields: FormFieldState[];
  submitAttempted: boolean;
  portalTarget?: Element | null;
  onChange: VariableCardProps['onChange'];
  tags: ReactNode;
  contentId: string;
  onCollapse: () => void;
}) => {
  const { t } = useTranslation();
  const selectId = useId();
  const [fieldTouched, setFieldTouched] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const showFieldError = (fieldTouched || submitAttempted) && !field;

  return (
    <div className="min-w-0 flex-1">
      <Label forId={selectId} size="small">
        {t('formPage.variableEditor.labelField')}
      </Label>
      <div className="flex flex-wrap items-center gap-150">
        <div className="min-w-0 flex-1 basis-full sm:basis-auto">
          <Combobox
            id={selectId}
            fullWidth
            size="medium"
            value={field?.id ? [field.id] : []}
            onChange={(_e, items) => {
              const nextField = fields.find((candidate) => candidate.id === items[0]?.value);
              setFieldTouched(true);
              onChange(variable.id, {
                fieldId: nextField?.id ?? '',
                labelFieldMapping: nextField?.name ?? '',
                type: inferVariableType(nextField),
              });
            }}
            placeholder={t('formPage.variableEditor.selectField')}
            required
            invalid={!!showFieldError}
            onOpenChange={setIsMenuOpen}
            {...(portalTarget ? { portalTarget } : {})}
          >
            {renderFieldOptions(fields)}
          </Combobox>
        </div>
        {tags}
        <div className="ml-auto">
          <UtilityButton
            iconName="chevron-up"
            size="small"
            appearance="ghost"
            aria-label={t('formPage.variableEditor.collapse')}
            aria-controls={contentId}
            aria-expanded
            onClick={onCollapse}
          />
        </div>
      </div>
      {field && !isMenuOpen && <FieldValidationInfo field={field} asTags className="mt-150" />}
      {showFieldError && (
        <HelpText variant="negative">{t('formPage.variableEditor.labelFieldRequired')}</HelpText>
      )}
    </div>
  );
};

const VariableTypeControl = ({
  variable,
  field,
  portalTarget,
  onChange,
}: {
  variable: FormVariableState;
  field: FormFieldState;
  portalTarget?: Element | null;
  onChange: VariableCardProps['onChange'];
}) => {
  const { t } = useTranslation();
  const typeOptions = getVariableTypeOptions(field);
  if (typeOptions.length < 2) return null;

  return (
    <FieldGroup label={t('formPage.variableEditor.type')}>
      <Combobox
        fullWidth
        size="medium"
        value={[variable.type || inferVariableType(field)]}
        onChange={(_e, items) => onChange(variable.id, { type: items[0]?.value ?? '' })}
        placeholder={t('formPage.variableEditor.type')}
        {...(portalTarget ? { portalTarget } : {})}
      >
        {typeOptions.map((type) => (
          <Combobox.Option key={type} value={type}>
            {type}
          </Combobox.Option>
        ))}
      </Combobox>
    </FieldGroup>
  );
};

const VariableCard = ({
  variable,
  fields,
  submitAttempted,
  portalTarget,
  number,
  onChange,
  onRemove,
}: VariableCardProps) => {
  const { t } = useTranslation();
  const contentId = useId();
  const [isOpen, setIsOpen] = useState(true);
  const field = fields.find((f) => f.id === variable.fieldId);

  const isConfigured =
    !!variable.name.trim() || !!variable.variableRule.trim() || variable.actions.length > 0;
  const statusTags = (
    <>
      {!field && variable.labelFieldMapping && (
        <Tag size="small" variant="negative">
          {variable.labelFieldMapping}
        </Tag>
      )}
      <Tag size="small" variant={isConfigured ? 'positive' : 'neutral'}>
        {t(
          isConfigured
            ? 'formPage.variableEditor.configured'
            : 'formPage.variableEditor.notConfigured'
        )}
      </Tag>
    </>
  );

  return (
    <Card appearance="outline">
      {isOpen ? (
        <div className="pl-300 pr-100 py-200">
          <CardFieldSelector
            variable={variable}
            field={field}
            fields={fields}
            submitAttempted={submitAttempted}
            portalTarget={portalTarget}
            onChange={onChange}
            tags={statusTags}
            contentId={contentId}
            onCollapse={() => setIsOpen(false)}
          />
        </div>
      ) : (
        <button
          type="button"
          className="flex w-full cursor-pointer items-center justify-between gap-150 pl-300 pr-100 py-200 text-left focus-visible:outline-2 focus-visible:outline-core-blue-700"
          aria-label={t('formPage.variableEditor.expand')}
          aria-controls={contentId}
          aria-expanded={false}
          onClick={() => setIsOpen(true)}
        >
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-150">
            <span className="min-w-0 flex-1 truncate text-body-md font-bold text-core-slate-1300">
              {field?.name ||
                variable.name ||
                t('formPage.variableEditor.variableHeader', { number })}
            </span>
            {field && (
              <Tag size="small" variant="neutral">
                {field.type}
              </Tag>
            )}
            {statusTags}
          </div>
          <Icon name="chevron-down" size="small" isChild />
        </button>
      )}

      <div id={contentId} className={cn('overflow-hidden', !isOpen && 'hidden')}>
        <div className="px-300 pb-300 flex flex-col gap-150">
          <FieldGroup
            label={t('formPage.variableEditor.variableName')}
            tooltip={t('formPage.variableEditor.variableNameHelp')}
          >
            <TextField
              size="medium"
              placeholder={t('formPage.variableEditor.variableNamePlaceholder')}
              value={variable.name}
              onChange={(_e, value) => onChange(variable.id, { ...variable, name: value })}
            />
          </FieldGroup>

          {field && (
            <VariableTypeControl
              variable={variable}
              field={field}
              portalTarget={portalTarget}
              onChange={onChange}
            />
          )}

          <FormVariableRegex
            variable={variable}
            portalTarget={portalTarget}
            onChange={(patch) => onChange(variable.id, { ...variable, ...patch })}
          />

          <VariableActionsPanel
            actions={variable.actions}
            onChange={(actions) => onChange(variable.id, { ...variable, actions })}
            isFieldRequired={field?.required}
            selectedField={field}
          />

          <div className="flex justify-end">
            <Button
              variant="negative"
              appearance="ghost"
              size="small"
              iconName="trash"
              onClick={() => onRemove(variable.id)}
              aria-label={t('formPage.variableEditor.removeVariableFieldAria', {
                field:
                  field?.name ||
                  variable.labelFieldMapping ||
                  variable.name ||
                  t('formPage.variableEditor.variableHeader', { number }),
              })}
            >
              {t('formPage.variableEditor.removeVariable')}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

type FormVariableEditorProps = {
  variables: FormVariableState[];
  fields: FormFieldState[];
  submitAttempted?: boolean;
  onVariableChange: (id: string, value: Record<string, unknown>) => void;
  onRemoveVariable: (id: string) => void;
  onAddVariable: (field: FormFieldState) => void;
  portalTarget?: Element | null;
};

export const FormVariableEditor = ({
  variables,
  fields,
  submitAttempted = false,
  onVariableChange,
  onRemoveVariable,
  onAddVariable,
  portalTarget,
}: FormVariableEditorProps) => {
  const { t } = useTranslation();
  const addSelectId = useId();
  const [isPickingField, setIsPickingField] = useState(false);
  const availableFields = fields.filter(
    (field) => field.id && !variables.some((variable) => variable.fieldId === field.id)
  );
  const cannotAddVariable = availableFields.length === 0 || variables.length >= fields.length;

  const firstCardRef = useRef<HTMLDivElement | null>(null);
  const prevCount = useRef(variables.length);
  useEffect(() => {
    if (variables.length > prevCount.current) {
      firstCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    prevCount.current = variables.length;
  }, [variables.length]);

  return (
    <div className="flex flex-col gap-200">
      <div>
        <Button
          variant="primary"
          appearance="outline"
          size="medium"
          className={cn('text-[var(--ds-color-interactive-primary-enabled)]!')}
          disabled={cannotAddVariable}
          aria-expanded={isPickingField && !cannotAddVariable}
          aria-controls={isPickingField && !cannotAddVariable ? addSelectId : undefined}
          onClick={() => setIsPickingField((open) => !open)}
        >
          {t('formPage.action.addNewVariable')}
        </Button>
        {isPickingField && !cannotAddVariable && (
          <div id={addSelectId} className="mt-150">
            <Label size="small" forId={`${addSelectId}-picker`}>
              {t('formPage.variableEditor.labelField')}
            </Label>
            <Combobox
              id={`${addSelectId}-picker`}
              fullWidth
              size="medium"
              value={[]}
              onChange={(_e, items) => {
                const field = availableFields.find((candidate) => candidate.id === items[0]?.value);
                if (field) {
                  onAddVariable(field);
                  setIsPickingField(false);
                }
              }}
              placeholder={t('formPage.variableEditor.searchFieldPlaceholder')}
              disabled={cannotAddVariable}
              {...(portalTarget ? { portalTarget } : {})}
            >
              {renderFieldOptions(availableFields)}
            </Combobox>
          </div>
        )}
      </div>
      {[...variables].reverse().map((variable, i) => (
        <div key={variable.id} ref={i === 0 ? firstCardRef : undefined}>
          <VariableCard
            variable={variable}
            fields={fields.filter(
              (field) =>
                field.id === variable.fieldId ||
                !variables.some((other) => other.id !== variable.id && other.fieldId === field.id)
            )}
            submitAttempted={submitAttempted}
            portalTarget={portalTarget}
            number={i + 1}
            onChange={onVariableChange}
            onRemove={onRemoveVariable}
          />
        </div>
      ))}
    </div>
  );
};
